import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { FileText, CheckCircle, XCircle } from 'lucide-react'
const API = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')
async function publicRpc(fn: string, args: Record<string, any> = {}) {
  const res = await fetch(`${API}/api/public/rpc/${fn}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  })
  return res.json()
}
import { ChangeRequestRow, ChangePlanRow, SecurityReviewRow } from '@/types'
import { useTranslation } from 'react-i18next'

export const PublicChangeRequestPage: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  
  const [loading, setLoading] = useState(true)
  const [validating, setValidating] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [tokenValid, setTokenValid] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [hasValidated, setHasValidated] = useState(false)
  
  const [formData, setFormData] = useState<Partial<ChangeRequestRow>>({
    type: 'hardware',
    request_date: new Date().toISOString().slice(0, 16),
    requester_department: '',
    requester_position: '',
    requester_name: '',
    installed_cbs: '',
    installed_component: '',
    reason: '',
    before_vendor_model: '',
    before_item_name: '',
    before_os_name_version: '',
    before_sw_version: '',
    after_vendor_model: '',
    after_item_name: '',
    after_os_name_version: '',
    after_sw_version: '',
    work_description: '',
    requestor_security_note: ''
  })
  
  const [changePlanData, setChangePlanData] = useState<Partial<ChangePlanRow>>({
    plan_date: '',
    author_department: '',
    author_position: '',
    author_name: '',
    plan_details: '',
    plan_security_comment: ''
  })
  
  const [securityReviewData, setSecurityReviewData] = useState<Partial<SecurityReviewRow>>({
    report_date: '',
    reviewer_department: '',
    reviewer_position: '',
    reviewer_name: '',
    review_date: '',
    overall_comment: '',
    items: null
  })

  const validateToken = useCallback(async () => {
    if (!token) {
      console.log('[MOC DEBUG] No token provided')
      return
    }
    
    try {
      console.log('[MOC DEBUG] Starting token validation for token:', token.substring(0, 8) + '...')
      setValidating(true)
      setLoading(true)
      setError(null)
      
      // add timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      )
      
      // use the security definer function to validate token (bypasses rls)
      console.log('[MOC DEBUG] Calling validate_submission_token RPC')
      const validationPromise = publicRpc('validate_submission_token', { token_text: token })
      
      const { data: tokenValidation, error: tokenError } = await Promise.race([
        validationPromise,
        timeoutPromise
      ]) as any

      console.log('[MOC DEBUG] Token validation result:', { 
        hasData: !!tokenValidation, 
        dataLength: tokenValidation?.length,
        error: tokenError 
      })

      if (tokenError) {
        console.error('[MOC DEBUG] Token validation error:', tokenError)
        setError(`Invalid or expired submission token: ${tokenError.message || 'Unknown error'}`)
        setTokenValid(false)
        setValidating(false)
        setLoading(false)
        return
      }

      if (!tokenValidation || tokenValidation.length === 0) {
        console.error('[MOC DEBUG] Token validation returned empty result')
        setError('Invalid or expired submission token')
        setTokenValid(false)
        setValidating(false)
        setLoading(false)
        return
      }

      const { change_request_id } = tokenValidation[0]
      console.log('[MOC DEBUG] Token validated successfully:', { change_request_id })

      // get the change request using rpc function
      console.log('[MOC DEBUG] Calling get_change_request_by_token RPC')
      const fetchPromise = publicRpc('get_change_request_by_token', { token_text: token })
      
      const { data: crDataJson, error: crError } = await Promise.race([
        fetchPromise,
        timeoutPromise
      ]) as any

      console.log('[MOC DEBUG] Change request fetch result:', { 
        hasData: !!crDataJson, 
        error: crError 
      })

      if (crError) {
        console.error('[MOC DEBUG] Error fetching change request:', crError)
        // if it's an rls error, provide a more helpful message
        if (crError.code === '42501' || crError.message?.includes('policy')) {
          setError('Unable to access change request. The submission link may have expired.')
        } else if (crError.message?.includes('timeout')) {
          setError('Request timed out. Please try again.')
        } else if (crError.message?.includes('not found')) {
          setError('Change request not found')
        } else {
          setError(`Failed to load change request: ${crError.message || 'Unknown error'}`)
        }
        setTokenValid(false)
        setValidating(false)
        setLoading(false)
        return
      }

      // parse jsonb response
      const crData = typeof crDataJson === 'string' ? JSON.parse(crDataJson) : crDataJson
      console.log('[MOC DEBUG] Parsed change request data:', { 
        hasData: !!crData, 
        id: crData?.id 
      })

      if (!crData) {
        console.error('[MOC DEBUG] Change request data is null or empty')
        setError('Change request not found')
        setTokenValid(false)
        setValidating(false)
        setLoading(false)
        return
      }

      setTokenValid(true)
      console.log('[MOC DEBUG] Token validation successful, loading form data')
      
      // load existing change request data
      setFormData({
        ...crData,
        request_date: crData.request_date ? new Date(crData.request_date).toISOString().slice(0, 16) : null
      })
      
      // load change plan if it exists
      try {
        const { data: cpDataJson, error: cpError } = await publicRpc('get_change_plan_by_token', { token_text: token })
        
        if (!cpError && cpDataJson) {
          const cpData = typeof cpDataJson === 'string' ? JSON.parse(cpDataJson) : cpDataJson
          if (cpData) {
            setChangePlanData({
              plan_date: cpData.plan_date ? new Date(cpData.plan_date).toISOString().slice(0, 16) : '',
              author_department: cpData.author_department || '',
              author_position: cpData.author_position || '',
              author_name: cpData.author_name || '',
              plan_details: cpData.plan_details || '',
              plan_security_comment: cpData.plan_security_comment || ''
            })
          }
        }
      } catch (err) {
        console.warn('[MOC DEBUG] Error loading change plan:', err)
      }
      
      // load security review if it exists
      try {
        const { data: srDataJson, error: srError } = await publicRpc('get_security_review_by_token', { token_text: token })
        
        if (!srError && srDataJson) {
          const srData = typeof srDataJson === 'string' ? JSON.parse(srDataJson) : srDataJson
          if (srData) {
            setSecurityReviewData({
              report_date: srData.report_date ? new Date(srData.report_date).toISOString().slice(0, 16) : '',
              reviewer_department: srData.reviewer_department || '',
              reviewer_position: srData.reviewer_position || '',
              reviewer_name: srData.reviewer_name || '',
              review_date: srData.review_date ? new Date(srData.review_date).toISOString().slice(0, 16) : '',
              overall_comment: srData.overall_comment || '',
              items: srData.items || null
            })
          }
        }
      } catch (err) {
        console.warn('[MOC DEBUG] Error loading security review:', err)
      }
      
      console.log('[MOC DEBUG] Form data loaded successfully')
    } catch (err: any) {
      console.error('[MOC DEBUG] Error validating token:', err)
      console.error('[MOC DEBUG] Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      })
      if (err.message?.includes('timeout')) {
        setError('Request timed out. Please check your connection and try again.')
      } else {
        setError(`Failed to validate submission token: ${err.message || 'Unknown error'}. Please check the link and try again.`)
      }
      setTokenValid(false)
    } finally {
      setValidating(false)
      setLoading(false)
      console.log('[MOC DEBUG] Token validation process completed')
    }
  }, [token, t])

  useEffect(() => {
    if (token && !hasValidated) {
      setHasValidated(true)
      validateToken()
    } else if (!token) {
      setError(t('publicWorkflow.noToken'))
      setValidating(false)
      setLoading(false)
    }
  }, [token, hasValidated, validateToken])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token || !tokenValid) return

    setSubmitting(true)
    setError(null)

    try {
      console.log('[MOC DEBUG] Starting form submission')
      // use the rpc function to update change request
      const updateData = {
        ...formData,
        request_date: formData.request_date ? new Date(formData.request_date).toISOString() : null
      }

      // remove fields that shouldn't be updated by vendor
      // vendors can only update basic form fields, not status/approval fields or workflow fields
      delete (updateData as any).id

      delete (updateData as any).ship_id
      delete (updateData as any).created_by
      delete (updateData as any).created_at
      delete (updateData as any).submission_token_id
      delete (updateData as any).updated_at  // rpc function sets this automatically
      // workflow fields - only internal users can change these
      delete (updateData as any).status
      delete (updateData as any).approval_status
      delete (updateData as any).approved_by
      delete (updateData as any).approved_at
      delete (updateData as any).rejection_reason
      delete (updateData as any).implemented_by
      delete (updateData as any).implemented_at
      // e22 §6 fields - only internal users can fill these
      delete (updateData as any).category
      delete (updateData as any).criticality
      delete (updateData as any).system_category
      delete (updateData as any).risk_level
      delete (updateData as any).impact_summary
      delete (updateData as any).requires_class_approval
      delete (updateData as any).analysis_by
      delete (updateData as any).analysis_at
      delete (updateData as any).backup_completed
      delete (updateData as any).backup_details
      delete (updateData as any).rollback_plan
      delete (updateData as any).rollback_possible
      delete (updateData as any).rollback_executed
      delete (updateData as any).rollback_reason
      delete (updateData as any).rollback_at
      delete (updateData as any).pre_test_required
      delete (updateData as any).pre_test_summary
      delete (updateData as any).pre_test_evidence
      delete (updateData as any).post_test_required
      delete (updateData as any).post_test_summary
      delete (updateData as any).post_test_evidence
      delete (updateData as any).test_summary
      delete (updateData as any).software_artifact_ref
      delete (updateData as any).checksum_or_signature
      delete (updateData as any).config_version
      delete (updateData as any).vendor_notified
      delete (updateData as any).vendor_name
      delete (updateData as any).shipyard_notified
      delete (updateData as any).class_approval_ref
      delete (updateData as any).other_stakeholders
      delete (updateData as any).post_implementation_review
      delete (updateData as any).cab_review

      console.log('[MOC DEBUG] Calling update_change_request_by_token RPC with data:', Object.keys(updateData))
      const { data: updatedData, error: updateError } = await publicRpc('update_change_request_by_token', {
        token_text: token,
        update_data: updateData
      })

      console.log('[MOC DEBUG] Update result:', { hasData: !!updatedData, error: updateError })

      if (updateError) {
        console.error('[MOC DEBUG] Update error:', updateError)
        // if it's a policy error, the token might have been invalidated
        if (updateError.code === '42501') {
          throw new Error('Unable to submit. The submission link may have expired or been deactivated.')
        }
        throw updateError
      }

      // submit change plan if data exists
      const hasChangePlanData = changePlanData.plan_date || changePlanData.author_department || 
        changePlanData.author_position || changePlanData.author_name || 
        changePlanData.plan_details || changePlanData.plan_security_comment
      
      if (hasChangePlanData) {
        const planPayload = {
          plan_date: changePlanData.plan_date ? new Date(changePlanData.plan_date).toISOString() : null,
          author_department: changePlanData.author_department || null,
          author_position: changePlanData.author_position || null,
          author_name: changePlanData.author_name || null,
          plan_details: changePlanData.plan_details || null,
          plan_security_comment: changePlanData.plan_security_comment || null
        }
        
        const { error: planError } = await publicRpc('upsert_change_plan_by_token', {
          token_text: token,
          plan_data: planPayload
        })
        
        if (planError) {
          console.error('[MOC DEBUG] Error upserting change plan:', planError)
          throw new Error(`Failed to save change plan: ${planError.message}`)
        }
      }
      
      // submit security review if data exists
      const hasSecurityReviewData = securityReviewData.report_date || securityReviewData.reviewer_department ||
        securityReviewData.reviewer_position || securityReviewData.reviewer_name ||
        securityReviewData.review_date || securityReviewData.overall_comment
      
      if (hasSecurityReviewData) {
        const reviewPayload = {
          report_date: securityReviewData.report_date ? new Date(securityReviewData.report_date).toISOString() : null,
          reviewer_department: securityReviewData.reviewer_department || null,
          reviewer_position: securityReviewData.reviewer_position || null,
          reviewer_name: securityReviewData.reviewer_name || null,
          review_date: securityReviewData.review_date ? new Date(securityReviewData.review_date).toISOString() : null,
          overall_comment: securityReviewData.overall_comment || null,
          items: securityReviewData.items || null
        }
        
        const { error: reviewError } = await publicRpc('upsert_security_review_by_token', {
          token_text: token,
          review_data: reviewPayload
        })
        
        if (reviewError) {
          console.error('[MOC DEBUG] Error upserting security review:', reviewError)
          throw new Error(`Failed to save security review: ${reviewError.message}`)
        }
      }
      
      console.log('[MOC DEBUG] Form submitted successfully')
      setSuccess(true)
    } catch (err: any) {
      console.error('[MOC DEBUG] Error submitting change request:', err)
      console.error('[MOC DEBUG] Error details:', {
        message: err.message,
        code: err.code,
        details: err.details,
        hint: err.hint
      })
      setError(err.message || 'Failed to submit change request')
    } finally {
      setSubmitting(false)
      console.log('[MOC DEBUG] Form submission process completed')
    }
  }

  if (loading || validating) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">{t('publicWorkflow.validating')}</p>
        </div>
      </div>
    )
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-8 text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('publicWorkflow.invalidTitle')}</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-4">{error || t('publicWorkflow.invalidDescription')}</p>
            <p className="text-sm text-slate-500 dark:text-slate-500">
              {t('publicWorkflow.contactOrganization')}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('moc.submittedTitle')}</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {t('moc.submittedDescription')}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-500">
              {t('publicWorkflow.additionalInfo')}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white">
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="px-6 py-4">
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            {t('moc.submitTitle')}
          </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('moc.submitSubtitle')}
            </p>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
        {error && (
          <Card className="mb-6 border-red-200 dark:border-red-800">
            <CardContent className="py-4">
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>{t('moc.information')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('moc.type')} *
                    </label>
                    <select
                      value={formData.type || 'hardware'}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'hardware' | 'software' }))}
                      className="flex h-10 w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      required
                    >
                      <option value="hardware">{t('fields.hardware')}</option>
                      <option value="software">{t('fields.software')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('firewall.requestDate')}
                    </label>
                    <Input
                      type="datetime-local"
                      value={formData.request_date || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, request_date: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('firewall.requesterDepartment')}
                    </label>
                    <Input
                      value={formData.requester_department || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, requester_department: e.target.value }))}
                      placeholder={t('firewall.department')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('firewall.requesterPosition')}
                    </label>
                    <Input
                      value={formData.requester_position || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, requester_position: e.target.value }))}
                      placeholder={t('firewall.position')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('firewall.requesterName')} *
                    </label>
                    <Input
                      value={formData.requester_name || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, requester_name: e.target.value }))}
                      placeholder={t('firewall.name')}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('moc.installedCbs')}
                    </label>
                    <Input
                      value={formData.installed_cbs || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, installed_cbs: e.target.value }))}
                      placeholder={t('moc.installedCbs')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('firewall.installedComponent')}
                    </label>
                    <Input
                      value={formData.installed_component || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, installed_component: e.target.value }))}
                      placeholder={t('firewall.installedComponent')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('firewall.reason')} *
                  </label>
                  <textarea
                    value={formData.reason || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder={t('moc.reasonPlaceholder')}
                    className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    required
                  />
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-medium mb-4">{t('firewall.beforeChange')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {t('firewall.vendorModel')}
                      </label>
                      <Input
                        value={formData.before_vendor_model || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, before_vendor_model: e.target.value }))}
                        placeholder={t('firewall.vendorModel')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {t('moc.itemName')}
                      </label>
                      <Input
                        value={formData.before_item_name || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, before_item_name: e.target.value }))}
                        placeholder={t('moc.itemName')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {t('moc.osVersion')}
                      </label>
                      <Input
                        value={formData.before_os_name_version || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, before_os_name_version: e.target.value }))}
                        placeholder={t('moc.osVersion')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {t('moc.softwareVersion')}
                      </label>
                      <Input
                        value={formData.before_sw_version || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, before_sw_version: e.target.value }))}
                        placeholder={t('moc.softwareVersion')}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-medium mb-4">{t('firewall.afterChange')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {t('firewall.vendorModel')}
                      </label>
                      <Input
                        value={formData.after_vendor_model || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, after_vendor_model: e.target.value }))}
                        placeholder={t('firewall.vendorModel')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {t('moc.itemName')}
                      </label>
                      <Input
                        value={formData.after_item_name || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, after_item_name: e.target.value }))}
                        placeholder={t('moc.itemName')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {t('moc.osVersion')}
                      </label>
                      <Input
                        value={formData.after_os_name_version || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, after_os_name_version: e.target.value }))}
                        placeholder={t('moc.osVersion')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {t('moc.softwareVersion')}
                      </label>
                      <Input
                        value={formData.after_sw_version || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, after_sw_version: e.target.value }))}
                        placeholder={t('moc.softwareVersion')}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('firewall.workDescription')}
                  </label>
                  <textarea
                    value={formData.work_description || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, work_description: e.target.value }))}
                    placeholder={t('firewall.workPlaceholder')}
                    className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('moc.securityNotes')}
                  </label>
                  <textarea
                    value={formData.requestor_security_note || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, requestor_security_note: e.target.value }))}
                    placeholder={t('moc.securityNotesPlaceholder')}
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* change plan section */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>{t('moc.changePlan')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('moc.planDate')}
                    </label>
                    <Input
                      type="datetime-local"
                      value={changePlanData.plan_date || ''}
                      onChange={(e) => setChangePlanData(prev => ({ ...prev, plan_date: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('moc.authorDepartment')}
                    </label>
                    <Input
                      value={changePlanData.author_department || ''}
                      onChange={(e) => setChangePlanData(prev => ({ ...prev, author_department: e.target.value }))}
                      placeholder={t('firewall.department')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('moc.authorPosition')}
                    </label>
                    <Input
                      value={changePlanData.author_position || ''}
                      onChange={(e) => setChangePlanData(prev => ({ ...prev, author_position: e.target.value }))}
                      placeholder={t('firewall.position')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('moc.authorName')}
                    </label>
                    <Input
                      value={changePlanData.author_name || ''}
                      onChange={(e) => setChangePlanData(prev => ({ ...prev, author_name: e.target.value }))}
                      placeholder={t('firewall.name')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('moc.planDetails')}
                  </label>
                  <textarea
                    value={changePlanData.plan_details || ''}
                    onChange={(e) => setChangePlanData(prev => ({ ...prev, plan_details: e.target.value }))}
                    placeholder={t('moc.planDetailsPlaceholder')}
                    className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('moc.planSecurityComment')}
                  </label>
                  <textarea
                    value={changePlanData.plan_security_comment || ''}
                    onChange={(e) => setChangePlanData(prev => ({ ...prev, plan_security_comment: e.target.value }))}
                    placeholder={t('moc.planSecurityPlaceholder')}
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* security review section */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>{t('firewall.securityReview')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('moc.reportDate')}
                    </label>
                    <Input
                      type="datetime-local"
                      value={securityReviewData.report_date || ''}
                      onChange={(e) => setSecurityReviewData(prev => ({ ...prev, report_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('moc.reviewDate')}
                    </label>
                    <Input
                      type="datetime-local"
                      value={securityReviewData.review_date || ''}
                      onChange={(e) => setSecurityReviewData(prev => ({ ...prev, review_date: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('moc.reviewerDepartment')}
                    </label>
                    <Input
                      value={securityReviewData.reviewer_department || ''}
                      onChange={(e) => setSecurityReviewData(prev => ({ ...prev, reviewer_department: e.target.value }))}
                      placeholder={t('firewall.department')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('moc.reviewerPosition')}
                    </label>
                    <Input
                      value={securityReviewData.reviewer_position || ''}
                      onChange={(e) => setSecurityReviewData(prev => ({ ...prev, reviewer_position: e.target.value }))}
                      placeholder={t('firewall.position')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('moc.reviewerName')}
                    </label>
                    <Input
                      value={securityReviewData.reviewer_name || ''}
                      onChange={(e) => setSecurityReviewData(prev => ({ ...prev, reviewer_name: e.target.value }))}
                      placeholder={t('firewall.name')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('moc.overallComment')}
                  </label>
                  <textarea
                    value={securityReviewData.overall_comment || ''}
                    onChange={(e) => setSecurityReviewData(prev => ({ ...prev, overall_comment: e.target.value }))}
                    placeholder={t('moc.overallCommentPlaceholder')}
                    className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting ? t('publicWorkflow.submitting') : t('moc.submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

